import { createContext, useContext, useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeListener = null;

    // Listen for auth state so we can setup/teardown notifications listener
    const authUnsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setNotifications([]);
        setNotificationCount(0);
        setLoading(false);
        if (unsubscribeListener) {
          unsubscribeListener();
          unsubscribeListener = null;
        }
        return;
      }

      // Real-time listener for notifications
      const notifQuery = query(
        collection(db, "notifications"),
        where("userId", "==", user.uid)
      );

      if (unsubscribeListener) {
        unsubscribeListener();
      }

      unsubscribeListener = onSnapshot(
        notifQuery,
        async (snapshot) => {
          try {
            const allNotifications = snapshot.docs.map((d) => ({
              id: d.id,
              ...d.data(),
            }));

            // Deduplicate by appointmentId (keep latest by createdAt)
            const latestByAppt = new Map();
            for (const n of allNotifications) {
              const key = n.appointmentId || n.id;
              const time = n.createdAt?.toMillis ? n.createdAt.toMillis() : 0;
              const prev = latestByAppt.get(key);
              const prevTime = prev?.createdAt?.toMillis
                ? prev.createdAt.toMillis()
                : 0;
              if (!prev || time >= prevTime) {
                latestByAppt.set(key, n);
              }
            }
            const deduped = Array.from(latestByAppt.values());

            // Filter out notifications for completed or cancelled appointments
            const filteredNotifications = [];
            const notificationsToDelete = [];

            for (const notif of deduped) {
              if (notif.appointmentId) {
                try {
                  const apptSnap = await getDoc(
                    doc(db, "appointments", notif.appointmentId)
                  );
                  if (apptSnap.exists()) {
                    const apptData = apptSnap.data();
                    // Skip if appointment is completed or cancelled
                    if (
                      apptData.status === "Completed" ||
                      apptData.status === "Cancelled"
                    ) {
                      notificationsToDelete.push(notif.id);
                      continue;
                    }
                  }
                } catch (error) {
                  console.error("Error checking appointment status:", error);
                }
              }
              filteredNotifications.push(notif);
            }

            // Delete notifications for completed/cancelled appointments
            for (const notifId of notificationsToDelete) {
              try {
                await deleteDoc(doc(db, "notifications", notifId));
              } catch (error) {
                console.error("Error deleting notification:", error);
              }
            }

            // Sort by createdAt descending
            filteredNotifications.sort((a, b) => {
              const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
              const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
              return bTime - aTime;
            });

            setNotifications(filteredNotifications);

            // Count unread notifications
            const unreadCount = filteredNotifications.filter(
              (n) => n.read === false
            ).length;
            setNotificationCount(unreadCount);
          } catch (error) {
            console.error("Error processing notifications:", error);
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          console.error("Error listening to notifications:", error);
          setLoading(false);
        }
      );
    });

    return () => {
      authUnsub();
      if (unsubscribeListener) unsubscribeListener();
    };
  }, []);

  // Mark a single notification as read
  const markNotificationAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, "notifications", notificationId), {
        read: true,
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Mark multiple notifications as read
  const markNotificationsAsRead = async (notificationIds) => {
    try {
      const promises = notificationIds.map((id) =>
        updateDoc(doc(db, "notifications", id), { read: true })
      );
      await Promise.all(promises);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  // Delete a notification
  const deleteNotification = async (notificationId) => {
    try {
      await deleteDoc(doc(db, "notifications", notificationId));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  // Delete multiple notifications
  const deleteNotifications = async (notificationIds) => {
    try {
      const promises = notificationIds.map((id) =>
        deleteDoc(doc(db, "notifications", id))
      );
      await Promise.all(promises);
    } catch (error) {
      console.error("Error deleting notifications:", error);
    }
  };

  // Check if notification has action buttons
  const hasActionButtons = (notif) => {
    // Notifications with type 'appointment_ended' or 'started' have action buttons
    return notif.type === "appointment_ended" || notif.type === "started";
  };

  const value = {
    notifications,
    notificationCount,
    loading,
    markNotificationAsRead,
    markNotificationsAsRead,
    deleteNotification,
    deleteNotifications,
    hasActionButtons,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
